// TODO: uncomment when functionality to upload plots in UI is fixed
import { Divider } from 'component-library';
import { useTranslation } from 'react-i18next';

import { BenchmarkType, IndicatorName, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { GraphFact } from '@/components/Charts/types';
import { IndicatorProvider } from '@/components/IndicatorCard/IndicatorContextProvider';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { BiodiversityExplainer } from '@/components/NCExplainers/BiodiversityExplainer';
import { BetaPill } from '@/components/Pill/BetaPill';
import { COMPLIANCE_LOGOS_BIODIVERSITY } from '@/config/constants';
import { useBenchmarkSelection } from '@/hooks/useBenchmarkSelection';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { getBenchmarksFromFacts, useBenchmarksForPlot } from '@/pages/shared/hooks/useBenchmarkForFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';

import { PlotCardWrapper } from '../PlotCardWrapper';
import { BiodiversityGraphTile } from './GraphTile';
import { BiodiversityStatsTile } from './StatsTile';

export const Biodiversity = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('global.analysis.protectedOnFarmHabitat')}>
      <IndicatorProvider>
        <BiodiversityPlotCard />
      </IndicatorProvider>
    </PlotCardWrapper>
  );
};

const BiodiversityPlotCard = () => {
  const { t } = useTranslation();

  const factNames = {
    graph: IndicatorName.biodiversity_buffer_zone_pct_historic_graph,
    latestAnalysis: IndicatorName.biodiversity_buffer_zone_pct,
    oneYearUplift: IndicatorName.biodiversity_buffer_zone_pct_previous_1y_uplift_change,
    benchmark: IndicatorName.biodiversity_buffer_zone_pct_benchmark_graph,
  };

  const facts = usePlotFacts(Object.values(factNames));

  const graphData = getFactFromFactArray<GraphFact[]>(facts, factNames.graph)?.value;
  const latestAnalysisFact = getFactFromFactArray(facts, factNames.latestAnalysis);
  const oneYearUpliftFact = getFactFromFactArray(facts, factNames.oneYearUplift);

  const highBenchmarkFact = getFactFromFactArray<GraphFact[]>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
  });
  const moderateBenchmarkFact = getFactFromFactArray<GraphFact[]>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
  });
  const lowBenchmarkFact = getFactFromFactArray<GraphFact[]>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
  });

  const graphBenchmarks = getBenchmarksFromFacts({
    highBenchmarkFact,
    moderateBenchmarkFact,
    lowBenchmarkFact,
  });

  const statsBenchmarks = useBenchmarksForPlot(factNames.benchmark);

  const organizationId = useOrganizationId();
  const indicator = GraphTileType.protected_on_farm_habitat;
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useBenchmarkSelection(indicator);

  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);

  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmarkId);

  const hasChartData = !isNullish(graphData);
  const hasStatsData = !isNullish(latestAnalysisFact?.value) || !isNullish(oneYearUpliftFact?.value);

  if (!hasChartData && !hasStatsData) {
    return null;
  }

  return (
    <NCCard explainer={<BiodiversityExplainer />} data-testid='biodiversity-nca'>
      <NCCardHeader
        title={
          <span className='flex flex-row items-center gap-3'>
            {t('global.analysis.protectedOnFarmHabitat')}
            <BetaPill
              tooltipBody={t('shared.ncaDetail.details.biodiversity.beta.tooltip.body')}
              tooltipTitle={t('shared.ncaDetail.details.biodiversity.beta.tooltip.title')}
            />
          </span>
        }
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <BiodiversityGraphTile
          data={graphData}
          benchmarks={graphBenchmarks}
          highBenchmark={highBenchmarkFact}
          moderateBenchmark={moderateBenchmarkFact}
          lowBenchmark={lowBenchmarkFact}
          activeCustomBenchmark={activeCustomBenchmark}
          selectedBenchmarkId={selectedBenchmarkId}
          onBenchmarkSelect={setSelectedBenchmarkId}
        />
        <BiodiversityStatsTile
          latestAnalysisFact={latestAnalysisFact}
          oneYearUpliftFact={oneYearUpliftFact}
          benchmarks={statsBenchmarks}
        />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_BIODIVERSITY} />
    </NCCard>
  );
};
